/**
 * @file ConsentPage.tsx
 * @brief "Claude wants to read your Verblizr meetings" — the OAuth consent screen.
 *
 * @details
 * Reached from BACKEND `/oauth/authorize` with `?request=<signed request>`.
 * The user is already signed in (RequireAuth sends them through /login and
 * back here). Allow → the backend mints a one-time code and we send the
 * browser back to the AI app; Cancel → the app is told "access_denied".
 *
 * The app's name is whatever it registered, so the page also shows the
 * address it will return to, which it cannot fake ("Claude" returning to
 * claude.ai is real; "Claude" returning to anything else is not).
 *
 * @references
 * - BACKEND/src/features/mcp/oauth.routes.mjs
 * - features/mcp-connect/ConnectedAiApps.tsx (where access is revoked)
 *
 * Z1 (27-10-2026): Zapier comes through the same screen; its title, lists
 * and footer come from the scopes it asked for (integrations/zapierCopy.ts).
 *
 * @author DES
 * @date 06-10-2026
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, LoaderCircle, X } from "lucide-react";
import { BrandIcon } from "@/components/brand/BrandIcon";
import { Button } from "@/components/ui/Button";
import { ApiClientError } from "@/shared/api/client";
import { consentCopy, isAutomationKind } from "@/features/integrations/zapierCopy";
import { useConsentDecision, useConsentRequest } from "./api";

function errorText(err: unknown): string {
  if (err instanceof ApiClientError && err.message) return err.message;
  return "This connection request is no longer valid. Start again from the AI app.";
}

export function ConsentPage() {
  const [params] = useSearchParams();
  const request = params.get("request") ?? "";
  const info = useConsentRequest(request);
  const decide = useConsentDecision();
  const [leaving, setLeaving] = useState(false);

  const onDecide = async (allow: boolean) => {
    try {
      const redirectTo = await decide.mutateAsync({ request, allow });
      setLeaving(true);
      window.location.assign(redirectTo);
    } catch {
      /* shown below */
    }
  };

  const busy = decide.isPending || leaving;
  // Z1/M1: Zapier and Make get their own words, built from the scopes asked for.
  const copy = info.data ? consentCopy(info.data.appName, info.data.kind, info.data.scopes) : null;
  const failed = !request || info.isError || decide.isError;

  return (
    <div className="flex min-h-screen items-center justify-center bg-app-page px-4 py-10 text-foreground">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <BrandIcon size={40} />
        </div>

        {info.isLoading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <LoaderCircle className="h-6 w-6 animate-spin" aria-label="Loading" />
          </div>
        ) : failed ? (
          <div className="text-center">
            <h1 className="text-xl font-semibold tracking-tight">Could not connect</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {errorText(decide.error ?? info.error)}
            </p>
          </div>
        ) : info.data?.blocked ? (
          // Company switched AI connectors off (MCP Phase 2, 06-10-2026).
          <div className="text-center">
            <h1 className="text-xl font-semibold tracking-tight">
              {info.data.appName} can't connect
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {info.data.blockedMessage ??
                (isAutomationKind(info.data.kind)
                  ? `Your company admin has not allowed ${info.data.appName}.`
                  : "Your company has turned off AI assistant connectors.")}
            </p>
            <div className="mt-8 flex justify-center">
              <Button disabled={busy} onClick={() => void onDecide(false)} className="gap-2">
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                Back to {info.data.appName}
              </Button>
            </div>
          </div>
        ) : info.data && copy ? (
          <>
            <h1 className="text-center text-xl font-semibold tracking-tight">
              {copy.title}
            </h1>
            <p className="mt-2 text-center text-sm text-muted-foreground">
              Signed in as {info.data.account.email}
              {info.data.appHost ? (
                <>
                  {" · "}returns to <span className="font-medium text-foreground">{info.data.appHost}</span>
                </>
              ) : null}
            </p>

            <div className="mt-8 space-y-6">
              <section>
                <h2 className="text-sm font-medium">It will be able to</h2>
                <ul className="mt-2 space-y-2">
                  {copy.can.map((t) => (
                    <li key={t} className="flex gap-2.5 text-sm">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" strokeWidth={2} />
                      {t}
                    </li>
                  ))}
                </ul>
              </section>
              <section>
                <h2 className="text-sm font-medium">It will not be able to</h2>
                <ul className="mt-2 space-y-2">
                  {copy.cannot.map((t) => (
                    <li key={t} className="flex gap-2.5 text-sm text-muted-foreground">
                      <X className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
                      {t}
                    </li>
                  ))}
                </ul>
              </section>
              <p className="text-xs text-muted-foreground">{copy.footer}</p>
            </div>

            <div className="mt-8 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="ghost"
                className="text-muted-foreground"
                disabled={busy}
                onClick={() => void onDecide(false)}
              >
                Cancel
              </Button>
              <Button disabled={busy} onClick={() => void onDecide(true)} className="gap-2">
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                Allow
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
